import './styles.scss';
import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconPencil, IconShare2, IconX } from '@tabler/icons-react';
import { Text, Input, Button } from '@fragments';

export default function FormBuilderHeader({
    formName,
    setFormName,
    formActive,
    setFormActive,
    saving,
    onSave,
    onActivate,
    onClose,
}) {
    const { t } = useTranslation('formsModals');
    const titleRef = useRef(null);
    const isComposing = useRef(false);

    // Sync external value → DOM without disturbing the cursor
    useEffect(() => {
        const el = titleRef.current;
        if (!el) return;
        if (document.activeElement === el) return;
        if (el.textContent !== (formName ?? '')) {
            el.textContent = formName ?? '';
        }
    }, [formName]);

    function handleTitleClick() {
        const el = titleRef.current;
        if (!el) return;
        el.focus();
        // Move cursor to end
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
    }

    return (
        <div className="form-builder-header">
            {/* ── Left: close + title ──────────────────────────── */}
            <div className="fbh-left">
                <button className="fbh-close-btn" onClick={onClose} title={t('common:actions.close')}>
                    <IconX size={18} />
                </button>

                <div className="fbh-divider" />

                <div className="fbh-title-wrapper" onClick={handleTitleClick}>
                    <span
                        ref={titleRef}
                        className="fbh-title-input"
                        contentEditable
                        suppressContentEditableWarning
                        data-placeholder={t('builder.header.namePlaceholder')}
                        onInput={e => {
                            if (!isComposing.current) setFormName(e.currentTarget.textContent);
                        }}
                        onCompositionStart={() => { isComposing.current = true; }}
                        onCompositionEnd={e => {
                            isComposing.current = false;
                            setFormName(e.currentTarget.textContent);
                        }}
                        onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                    />
                    <IconPencil size={14} className="fbh-edit-hint" />
                </div>
            </div>

            {/* ── Right: active switch + save ──────────────────── */}
            <div className="fbh-right">
                <div className="fbh-active-row">
                    <Text.span contrast={5}>{t('common:status.active')}</Text.span>
                    <Input.Switch
                        value={formActive}
                        onInput={val => val ? onActivate() : setFormActive(false)}
                    />
                </div>

                <div className="fbh-separator" />

                <Button
                    text={t('builder.header.saveButton')}
                    onClick={onSave}
                    loading={saving}
                    startIcon={<IconShare2/>}
                    size="medium"
                />
            </div>
        </div>
    );
}
